/**
 * "Values that did not match" for step 3 (f10.6, design D10): unmapped Resource
 * BU values (*Map to* a canonical PSU, country and location -- a mapping is
 * created at commit) and unresolved `Proposal: Unit` values (*Map to* a unit
 * or *Exclude*). Every rule and version check is the server's; the controls
 * render only when `capabilities.can_decide` says so.
 */

import { useState } from 'react';
import { Ban, Check, RotateCcw } from 'lucide-react';

import type { KimbleResourceBuItem, KimbleReview, KimbleUnitItem } from '@/api/client';
import { useDecideKimbleResourceBu, useDecideKimbleUnit } from '@/api/queries';

import { formatSourceRows } from '../usEngagements/usEngagementsView';
import { MATCHED, UNDECIDED, decisionPill, refusalMessage } from './kimbleDumpView';

export function UnmatchedKimbleCard({ cycleId, review }: { cycleId: string; review: KimbleReview }) {
  const decideBu = useDecideKimbleResourceBu(cycleId, review.batch.id);
  const decideUnit = useDecideKimbleUnit(cycleId, review.batch.id);
  const bus = review.resource_bus.filter((item) => item.decision.code !== MATCHED);
  const units = review.units.filter((item) => item.decision.code !== MATCHED);
  const undecidedUnits = units.filter((item) => item.decision.code === UNDECIDED).length;
  const undecidedBus = bus.filter((item) => item.decision.code === UNDECIDED).length;
  const canDecide = review.capabilities.can_decide;
  const error = decideBu.error ?? decideUnit.error;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Values that did not match</h3>
        {undecidedUnits > 0 ? (
          <span className="badge pill-warning">{undecidedUnits} unit(s) block the commit</span>
        ) : undecidedBus > 0 ? (
          <span className="badge pill-info">{undecidedBus} Resource BU left open</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        {error ? (
          <div className="alert alert-danger" role="alert">
            <div>
              <div className="a-body">{refusalMessage(error)}</div>
            </div>
          </div>
        ) : null}

        {bus.length === 0 && units.length === 0 && (
          <p className="hint">
            All {review.resource_bus.length} Resource BU values and {review.units.length || 'the'} unit values
            matched
          </p>
        )}

        {bus.length > 0 && (
          <div className="stack-sm">
            <div className="stat-label">Resource BU</div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Value in the dump</th>
                    <th>Decision</th>
                    <th>Map to</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {bus.map((item) => (
                    <ResourceBuRow
                      key={item.id}
                      item={item}
                      review={review}
                      canDecide={canDecide}
                      busy={decideBu.isPending}
                      onDecide={(body) => decideBu.mutate({ itemId: item.id, body })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hint">
              Unmapped Resource BU rows are committed with 0 default headcount and flagged on the forecast grid.
              A mapping you choose is added to the maintained table when you accept.
            </p>
          </div>
        )}

        {units.length > 0 && (
          <div className="stack-sm">
            <div className="stat-label">Proposal: Unit</div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th>Value in the dump</th>
                    <th>Decision</th>
                    <th>Map to</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {units.map((item) => (
                    <UnitRow
                      key={item.id}
                      item={item}
                      options={review.options.active_units}
                      canDecide={canDecide}
                      busy={decideUnit.isPending}
                      onDecide={(decision, unitId) =>
                        decideUnit.mutate({
                          itemId: item.id,
                          body: { decision, business_unit_id: unitId ?? null, expected_version: item.version },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="hint">A mapped spelling is remembered as an alias of that unit when you accept.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Shared with step 4 (f10.7): both steps serve the same Resource BU item shape. */
export interface MapToOptions {
  options: Pick<KimbleReview['options'], 'canonical_psus' | 'active_countries'>;
}

export function ResourceBuRow({
  item,
  review,
  canDecide,
  busy,
  onDecide,
}: {
  item: KimbleResourceBuItem;
  review: MapToOptions;
  canDecide: boolean;
  busy: boolean;
  onDecide: (body: {
    decision: 'MAP' | 'UNDECIDED';
    canonical_psu?: string | null;
    country_id?: string | null;
    location_name?: string | null;
    expected_version: number;
  }) => void;
}) {
  const [psu, setPsu] = useState(item.decided?.canonical_psu ?? '');
  const [country, setCountry] = useState(item.decided?.country?.id ?? '');
  const [location, setLocation] = useState(item.decided?.location_name ?? '');
  const undecided = item.decision.code === UNDECIDED;
  const listId = `kd-psu-${item.id}`;
  return (
    <tr>
      <td>
        <div className="us-eng-strong">{item.raw_value}</div>
        <div className="hint">
          {item.row_count} rows &middot; {formatSourceRows(item.sample_rows)}
        </div>
      </td>
      <td>
        <span className={decisionPill(item.decision.code)}>{item.decision.name}</span>
        {item.decided && (
          <div className="hint">
            → {item.decided.canonical_psu}
            {item.decided.country ? ` · ${item.decided.country.code}` : ''}
          </div>
        )}
      </td>
      <td>
        {canDecide && undecided ? (
          <div className="stack-sm">
            <input
              aria-label={`Canonical PSU for ${item.raw_value}`}
              placeholder="Canonical PSU"
              list={listId}
              maxLength={100}
              value={psu}
              onChange={(event) => setPsu(event.target.value)}
            />
            <datalist id={listId}>
              {review.options.canonical_psus.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
            <select
              aria-label={`Country for ${item.raw_value}`}
              value={country}
              onChange={(event) => setCountry(event.target.value)}
            >
              <option value="">No country</option>
              {review.options.active_countries.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.code} — {option.name}
                </option>
              ))}
            </select>
            <input
              aria-label={`Location for ${item.raw_value}`}
              placeholder="Location (optional)"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
          </div>
        ) : (
          <span className="hint">{item.decided ? item.decided.canonical_psu : '—'}</span>
        )}
      </td>
      <td>
        {canDecide && (
          <div className="row-actions">
            {undecided ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy || !psu.trim()}
                onClick={() =>
                  onDecide({
                    decision: 'MAP',
                    canonical_psu: psu.trim(),
                    country_id: country || null,
                    location_name: location.trim() || null,
                    expected_version: item.version,
                  })
                }
              >
                <Check aria-hidden="true" /> Map
              </button>
            ) : (
              <button
                type="button"
                className="icon-btn"
                title="Back to awaiting decision"
                aria-label="Undo decision"
                disabled={busy}
                onClick={() => onDecide({ decision: 'UNDECIDED', expected_version: item.version })}
              >
                <RotateCcw aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function UnitRow({
  item,
  options,
  canDecide,
  busy,
  onDecide,
}: {
  item: KimbleUnitItem;
  options: KimbleReview['options']['active_units'];
  canDecide: boolean;
  busy: boolean;
  onDecide: (decision: 'MAP' | 'EXCLUDE' | 'UNDECIDED', unitId?: string) => void;
}) {
  const [choice, setChoice] = useState(item.decided_business_unit?.id ?? '');
  const undecided = item.decision.code === UNDECIDED;
  return (
    <tr>
      <td>
        <div className="us-eng-strong">{item.raw_value}</div>
        <div className="hint">{item.row_count} rows</div>
      </td>
      <td>
        <span className={decisionPill(item.decision.code)}>{item.decision.name}</span>
        {item.decided_business_unit && <div className="hint">→ {item.decided_business_unit.name}</div>}
      </td>
      <td>
        {canDecide && undecided ? (
          <select
            aria-label={`Map ${item.raw_value} to`}
            value={choice}
            onChange={(event) => setChoice(event.target.value)}
          >
            <option value="">Choose a Business Unit…</option>
            {options.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="hint">{item.decided_business_unit?.name ?? '—'}</span>
        )}
      </td>
      <td>
        {canDecide && (
          <div className="row-actions">
            {undecided ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy || !choice}
                  onClick={() => onDecide('MAP', choice)}
                >
                  <Check aria-hidden="true" /> Map
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => onDecide('EXCLUDE')}
                >
                  <Ban aria-hidden="true" /> Exclude
                </button>
              </>
            ) : (
              <button
                type="button"
                className="icon-btn"
                title="Back to awaiting decision"
                aria-label="Undo decision"
                disabled={busy}
                onClick={() => onDecide('UNDECIDED')}
              >
                <RotateCcw aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
